import { vec3 } from 'gl-matrix';
import { useCallback, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import { useRegistrationState } from '~/hooks/registration/useRegistrationStore';
import {
    VoiBounds,
    selectedToolState,
    voiBoundsState,
    voiCheckboxState,
} from '~/state/atoms/toolsAtoms';
import ProjectedVoiBoxWithHandles from './ProjectedVoiBoxWithHandles';

/** Default VOI is this fraction of the secondary image extent. */
const DEFAULT_VOI_FRACTION = 0.5;

/**
 * Compute a centered default VOI at 50% of the secondary image extent.
 */
function computeDefaultVoiBounds(view: PlaneView): VoiBounds | null {
    const secondaryGrid = view.environment.secondaryImage?.grid;
    if (!secondaryGrid) {
        return null;
    }

    const { sizeX, sizeY, sizeZ } = secondaryGrid;
    const corners = [
        secondaryGrid.toWorldSpace(vec3.fromValues(0, 0, 0)),
        secondaryGrid.toWorldSpace(vec3.fromValues(sizeX, 0, 0)),
        secondaryGrid.toWorldSpace(vec3.fromValues(0, sizeY, 0)),
        secondaryGrid.toWorldSpace(vec3.fromValues(0, 0, sizeZ)),
        secondaryGrid.toWorldSpace(vec3.fromValues(sizeX, sizeY, 0)),
        secondaryGrid.toWorldSpace(vec3.fromValues(sizeX, 0, sizeZ)),
        secondaryGrid.toWorldSpace(vec3.fromValues(0, sizeY, sizeZ)),
        secondaryGrid.toWorldSpace(vec3.fromValues(sizeX, sizeY, sizeZ)),
    ];

    const imgMin = vec3.clone(corners[0]);
    const imgMax = vec3.clone(corners[0]);
    for (let i = 1; i < corners.length; i++) {
        vec3.min(imgMin, imgMin, corners[i]);
        vec3.max(imgMax, imgMax, corners[i]);
    }

    const centerX = (imgMin[0] + imgMax[0]) / 2;
    const centerY = (imgMin[1] + imgMax[1]) / 2;
    const centerZ = (imgMin[2] + imgMax[2]) / 2;
    const halfX = ((imgMax[0] - imgMin[0]) * DEFAULT_VOI_FRACTION) / 2;
    const halfY = ((imgMax[1] - imgMin[1]) * DEFAULT_VOI_FRACTION) / 2;
    const halfZ = ((imgMax[2] - imgMin[2]) * DEFAULT_VOI_FRACTION) / 2;

    return {
        minX: centerX - halfX,
        minY: centerY - halfY,
        minZ: centerZ - halfZ,
        maxX: centerX + halfX,
        maxY: centerY + halfY,
        maxZ: centerZ + halfZ,
    };
}

interface VoiSelectorOverlayWrapperProps {
    view: PlaneView | null;
    viewportOrientation: Orientations;
}

const VoiSelectorOverlay = ({
    view,
    viewportOrientation,
}: VoiSelectorOverlayWrapperProps) => {
    const [voiBounds, setVoiBounds] = useRecoilState(voiBoundsState);
    const registrationState = useRegistrationState();
    const hasSelectedRegistration =
        registrationState.selectedSopInstanceUid !== undefined;
    const selectedTool = useRecoilValue(selectedToolState);
    const voiCheckbox = useRecoilValue(voiCheckboxState);
    const isVoiToolSelected = selectedTool === 'registration' && voiCheckbox;

    useEffect(() => {
        if (!view || !isVoiToolSelected) {
            return;
        }

        const canvasElement =
            (view as { canvasElement?: HTMLCanvasElement }).canvasElement ??
            (view as { canvas?: HTMLCanvasElement }).canvas;

        if (!canvasElement) {
            return;
        }

        const observer = new ResizeObserver(() => {
            setVoiBounds((prev) => (prev ? { ...prev } : prev));
        });

        observer.observe(canvasElement);
        return () => observer.disconnect();
    }, [view, isVoiToolSelected, setVoiBounds]);

    const initializeVoiBounds = useCallback(() => {
        if (!view || !hasSelectedRegistration) {
            return;
        }

        // Keep existing bounds if already set (e.g. toggling off/on)
        if (voiBounds) {
            return;
        }

        const defaultBounds = computeDefaultVoiBounds(view);
        if (defaultBounds) {
            setVoiBounds(defaultBounds);
        }
    }, [hasSelectedRegistration, voiBounds, setVoiBounds, view]);

    useEffect(() => {
        if (voiCheckbox) {
            initializeVoiBounds();
        }
    }, [initializeVoiBounds, voiCheckbox]);

    const computeProjectedVoiBox = () => {
        if (!voiBounds || !view) {
            return null;
        }

        const projectedBoxEdges = [
            vec3.fromValues(voiBounds.minX, voiBounds.minY, voiBounds.minZ),
            vec3.fromValues(voiBounds.minX, voiBounds.minY, voiBounds.maxZ),
            vec3.fromValues(voiBounds.minX, voiBounds.maxY, voiBounds.minZ),
            vec3.fromValues(voiBounds.minX, voiBounds.maxY, voiBounds.maxZ),
            vec3.fromValues(voiBounds.maxX, voiBounds.minY, voiBounds.minZ),
            vec3.fromValues(voiBounds.maxX, voiBounds.minY, voiBounds.maxZ),
            vec3.fromValues(voiBounds.maxX, voiBounds.maxY, voiBounds.minZ),
            vec3.fromValues(voiBounds.maxX, voiBounds.maxY, voiBounds.maxZ),
        ];

        const projected = projectedBoxEdges.map((p) =>
            view.canvasCoordsFromPatientCoords(p)
        );

        const xs = projected.map((p) => p[0]);
        const ys = projected.map((p) => p[1]);

        const left = Math.min(...xs);
        const top = Math.min(...ys);
        const width = Math.max(...xs) - left;
        const height = Math.max(...ys) - top;

        return (
            <ProjectedVoiBoxWithHandles
                rect={{ left, top, width, height }}
                voi={voiBounds}
                setVoi={setVoiBounds}
                view={view}
                orientation={viewportOrientation}
            />
        );
    };

    const projectedVoiBox = computeProjectedVoiBox();

    return isVoiToolSelected ? projectedVoiBox : null;
};

export default VoiSelectorOverlay;
